import {
  breakpoints,
  Chip,
  Col,
  Dialog,
  DropdownMenu,
  Icon,
  Input,
  Inset,
  ListEmpty,
  ListRow,
  PageBody,
  Row,
  ScrollArea,
  Tappable,
  Text,
  Tooltip,
  useTheme,
} from "@rally/ui-kit";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { coalesced } from "../chat/activity-refresh.ts";
import { appCard, type ListedApp } from "../chat/app/app-resource.tsx";
import { useStack } from "../chat/panel/panels.tsx";
import { useLive } from "../chat/run/live.ts";
import { Feed } from "../feed/feed.tsx";
import { InviteBox } from "../invites/invite-box.tsx";
import { say } from "../localization/say.ts";
import { WhenLabel } from "../localization/when-label.tsx";
import { cancelRequests, isLatestRequest, nextRequest } from "../net/latest-request.ts";
import { act, read } from "../net/rally.ts";
import { goTo } from "../router.ts";
import { Swipe } from "../swipe.tsx";
import { ProjectAppEditor } from "./project-app-editor.tsx";
import { containsProjectQuery } from "./project-model.ts";

type ProjectConversation = { id: string; title: string | null; lastAt: string | null; mine: boolean };
type ProjectApp = {
  id: string;
  slug: string;
  name: string;
  description: string;
  placement: string;
  openMode: "card" | "embedded" | "focus" | "link";
  channel: string | null;
  operationalDataAvailable: boolean;
  url: string | null;
  hostingMode: string | null;
  healthStatus: string | null;
  sourceRepositories: string[] | null;
};
type ProjectContents = {
  conversations: ProjectConversation[];
  apps: ProjectApp[];
};
type ProjectHead = { id: string; name: string; description: string | null };
type WhereIAm = { projects: ProjectHead[] };

function SectionAction({
  icon,
  label,
  testID,
  onPress,
}: {
  icon: "search" | "message-square-plus" | "plus";
  label: string;
  testID: string;
  onPress: () => void;
}) {
  return (
    <Tooltip content={label} accessibilityLabel={label} testID={testID} onPress={onPress} below>
      <Col style={{ width: 44, height: 44, alignItems: "center", justifyContent: "center" }}>
        <Icon name={icon} size={17} color="text.secondary" />
      </Col>
    </Tooltip>
  );
}

function SummaryRow({
  icon,
  label,
  value,
}: {
  icon: "git-branch" | "external-link" | "circle-check";
  label: string;
  value: string;
}) {
  const _theme = useTheme();
  return (
    <Row gap={2}>
      <Icon name={icon} size={14} color="text.tertiary" />
      <Text typeName="caption" color="text.tertiary">
        {label}
      </Text>
      <Text typeName="caption" color="text.secondary" numberOfLines={1} style={{ flex: 1 }}>
        {value}
      </Text>
    </Row>
  );
}

export function ProjectScreen({ project }: { project: string }) {
  const theme = useTheme();
  const stack = useStack();
  const narrow = theme.isAtMost(breakpoints.phone);
  const [head, setHead] = useState<ProjectHead | null>(null);
  const [contents, setContents] = useState<ProjectContents | null>(null);
  const [loadedProject, setLoadedProject] = useState<string | null>(null);
  const request = useRef(0);
  const [failed, setFailed] = useState(false);
  const [creating, setCreating] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [conversationSearch, setConversationSearch] = useState(false);
  const [appSearch, setAppSearch] = useState(false);
  const [conversationQuery, setConversationQuery] = useState("");
  const [appQuery, setAppQuery] = useState("");
  const again = useCallback(() => {
    const identity = nextRequest(request);
    setFailed(false);
    void Promise.all([read<WhereIAm>("where.i.am"), read<ProjectContents>("project.contents", { project })])
      .then(([where, held]) => {
        if (!isLatestRequest(request, identity)) return;
        setHead(where.projects.find((one) => one.id === project) ?? null);
        setContents(held);
        setLoadedProject(project);
      })
      .catch(() => {
        if (!isLatestRequest(request, identity)) return;
        setHead(null);
        setContents(null);
        setLoadedProject(project);
        setFailed(true);
      });
  }, [project]);
  useEffect(() => {
    setHead(null);
    setContents(null);
    setLoadedProject(null);
    again();
    return () => {
      cancelRequests(request);
    };
  }, [again]);
  const refresh = useMemo(() => coalesced(again), [again]);
  useEffect(() => () => refresh.cancel(), [refresh]);
  useLive((notice) => {
    if (notice.project === project) refresh.notice();
  });
  const startConversation = async () => {
    try {
      const made = await act<{ conversation: string }>("project.addConversation", { project });
      goTo(`/c/${made.conversation}`);
    } catch {}
  };
  const conversations = useMemo(() => {
    return (contents?.conversations ?? []).filter((one) =>
      containsProjectQuery(one.title ?? "", conversationQuery),
    );
  }, [contents?.conversations, conversationQuery]);
  const apps = useMemo(() => {
    return (contents?.apps ?? []).filter((one) => containsProjectQuery(one.name, appQuery));
  }, [appQuery, contents?.apps]);
  const openApp = (app: ProjectApp) => {
    const listed: ListedApp = {
      ...app,
      context: "project",
      contextId: project,
      display: {},
      config: {},
    };
    stack.open(appCard(listed));
  };
  const editor = (
    <ProjectAppEditor
      project={project}
      onCancel={() => setCreating(false)}
      onCreated={() => {
        setCreating(false);
        again();
      }}
    />
  );
  if (loadedProject !== project)
    return (
      <Inset pad={5} testID="project-loading">
        <Text typeName="body" color="text.tertiary">
          {say("project.loading")}
        </Text>
      </Inset>
    );
  if (failed || !head || !contents)
    return (
      <Inset pad={5} testID="project-unavailable">
        <Text typeName="body" color="danger">
          {say("project.loadFailed")}
        </Text>
      </Inset>
    );
  return (
    <PageBody testID="project-screen">
      <Row gap={3}>
        <Col grow>
          <Text typeName="title" color="text.primary" numberOfLines={1}>
            {head?.name ?? ""}
          </Text>
          {head?.description ? (
            <Text typeName="body" color="text.secondary">
              {head.description}
            </Text>
          ) : null}
        </Col>
        <DropdownMenu
          align="right"
          testID="project-more"
          accessibilityLabel={say("head.more")}
          items={[{ label: say("chat.inviteHere"), icon: "user-plus", onPress: () => setInviting(true) }]}
          trigger={
            <Col style={{ width: 44, height: 44, alignItems: "center", justifyContent: "center" }}>
              <Icon name="more-horizontal" size={20} color="text.secondary" />
            </Col>
          }
        />
      </Row>
      <Col gap={3} testID="project-conversations">
        <Row gap={1}>
          <Text accessibilityRole="header" typeName="bodySemibold" color="text.primary" style={{ flex: 1 }}>
            {say("project.conversations")}
          </Text>
          <SectionAction
            icon="search"
            label={say("react.search")}
            testID="project-conversation-search"
            onPress={() => setConversationSearch((was) => !was)}
          />
          <SectionAction
            icon="message-square-plus"
            label={say("project.newConversation")}
            testID="project-new-conversation"
            onPress={() => void startConversation()}
          />
        </Row>
        {conversationSearch ? (
          <Input
            value={conversationQuery}
            onChangeText={setConversationQuery}
            autoFocus
            accessibilityLabel={say("sift.search")}
            placeholder={say("sift.search")}
            style={{
              minHeight: 44,
              borderBottomWidth: 1,
              borderBottomColor: theme.color["border.default"],
              color: theme.color["text.primary"],
            }}
          />
        ) : null}
        {conversations.length === 0 ? (
          <ListEmpty icon="message" line={say("project.noConversations")} />
        ) : (
          conversations.map((one, index) => (
            <ListRow
              key={one.id}
              testID={`project-conversation-${one.id}`}
              lead={<Icon name="message-square" size={17} color="text.tertiary" />}
              title={one.title || say("schedule.untitled")}
              tag={!one.mine ? <Chip muted>{say("project.notJoined")}</Chip> : undefined}
              action={one.lastAt ? <WhenLabel at={one.lastAt} /> : undefined}
              onPress={() => goTo(`/c/${one.id}`)}
              last={index === conversations.length - 1}
            />
          ))
        )}
      </Col>

      <Col gap={3} testID="project-apps">
        <Row gap={1}>
          <Text accessibilityRole="header" typeName="bodySemibold" color="text.primary" style={{ flex: 1 }}>
            {say("project.apps")}
          </Text>
          <SectionAction
            icon="search"
            label={say("react.search")}
            testID="project-app-search"
            onPress={() => setAppSearch((was) => !was)}
          />
          <SectionAction
            icon="plus"
            label={say("project.createApp")}
            testID="project-create-app"
            onPress={() => setCreating(true)}
          />
        </Row>
        {appSearch ? (
          <Input
            value={appQuery}
            onChangeText={setAppQuery}
            autoFocus
            accessibilityLabel={say("sift.search")}
            placeholder={say("sift.search")}
            style={{
              minHeight: 44,
              borderBottomWidth: 1,
              borderBottomColor: theme.color["border.default"],
              color: theme.color["text.primary"],
            }}
          />
        ) : null}
        {creating && !narrow ? editor : null}
        {apps.length === 0 && !creating ? (
          <Text typeName="body" color="text.tertiary">
            {say("project.noApps")}
          </Text>
        ) : (
          apps.map((app) => (
            <Tappable
              key={app.placement}
              testID={`project-app-${app.id}`}
              accessibilityRole="button"
              accessibilityLabel={`${say("apps.open")}: ${app.name}`}
              onPress={() => openApp(app)}
              onLongPress={() => goTo(`/apps/${app.id}`)}
              style={{
                gap: theme.space[2],
                padding: theme.space[3],
                borderWidth: 1,
                borderColor: theme.color["border.default"],
                borderRadius: theme.radius.lg,
              }}
            >
              <Row gap={2}>
                <Icon name="box" size={18} color="accent" />
                <Text typeName="bodySemibold" color="text.primary" numberOfLines={1} style={{ flex: 1 }}>
                  {app.name}
                </Text>
                <Tappable
                  accessibilityRole="link"
                  accessibilityLabel={say("app.where")}
                  testID={`app-standing-${app.id}`}
                  onPress={(event) => {
                    event.stopPropagation();
                    goTo(`/apps/${app.id}`);
                  }}
                  style={{ width: 44, height: 44, alignItems: "center", justifyContent: "center" }}
                >
                  <Icon name="settings" size={16} color="text.tertiary" />
                </Tappable>
                <Icon name="chevron-right" size={16} color="text.tertiary" />
              </Row>
              {app.description ? (
                <Text typeName="caption" color="text.secondary" numberOfLines={2}>
                  {app.description}
                </Text>
              ) : null}
              <SummaryRow
                icon="git-branch"
                label={`${say("project.source")}:`}
                value={
                  !app.operationalDataAvailable
                    ? say("project.unavailable")
                    : app.sourceRepositories?.length
                      ? app.sourceRepositories.join(", ")
                      : say("project.noSource")
                }
              />
              <SummaryRow
                icon="external-link"
                label={`${say("project.deployment")}:`}
                value={
                  !app.operationalDataAvailable
                    ? say("project.unavailable")
                    : app.hostingMode
                      ? say(`project.hosting.${app.hostingMode}` as "project.hosting.external-managed")
                      : say("project.notDeployed")
                }
              />
              <SummaryRow
                icon="circle-check"
                label={`${say("project.health")}:`}
                value={
                  !app.operationalDataAvailable
                    ? say("project.unavailable")
                    : say(`project.health.${app.healthStatus ?? "unknown"}` as "project.health.unknown")
                }
              />
            </Tappable>
          ))
        )}
      </Col>

      <Col gap={3} testID="project-activity">
        <Text accessibilityRole="header" typeName="bodySemibold" color="text.primary">
          {say("project.activity")}
        </Text>
        <Feed scope="project" id={project} limit={20} />
      </Col>

      {creating && narrow ? (
        <Swipe onDismiss={() => setCreating(false)} testID="project-create-layer" zIndex={50}>
          <ScrollArea contentContainerStyle={{ padding: theme.space[4] }}>{editor}</ScrollArea>
        </Swipe>
      ) : null}
      {inviting && head ? (
        <Dialog visible title={head.name} testID="project-invite" onDismiss={() => setInviting(false)}>
          <InviteBox place={{ kind: "project", id: project, name: head.name }} />
        </Dialog>
      ) : null}
    </PageBody>
  );
}
